import { useEffect, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';

/**
 * A média do apartamento da obra aberta, para a tela dos itens (Fase 40, RN-520 a RN-522): a
 * média ao lado do índice, o "Usar a média" e a faixa "a média mudou".
 *
 * Lida uma vez por obra: a média muda quando outra obra muda, e não a cada tecla desta. Quem só
 * vê o quantitativo não a pede — o servidor pede `takeoff.manage`, e quem não pode mexer no
 * índice não precisa do botão que o troca. `undefined` é "sem média": lendo, sem permissão, sem
 * referência ou a leitura falhou — a tela fica como antes da Fase 40.
 */
export function useMediaDoApartamento(): ApartmentAverageForProject | undefined {
  const container = useContainer();
  const { session, activeProjectId: projectId } = useSessionState();
  const podeEditar = usePermission(PERMISSION.TAKEOFF_MANAGE, projectId);
  const [media, setMedia] = useState<ApartmentAverageForProject | undefined>(undefined);

  useEffect(() => {
    setMedia(undefined);
    if (session === undefined || projectId === undefined || !podeEditar) return;
    let vivo = true;
    // Pela promessa, para a falha síncrona também virar "sem média", e não quebrar a tela.
    Promise.resolve()
      .then(() => container.getApartmentAverage.execute({ projectId }, session))
      .then((resultado) => {
        if (vivo && resultado.ok && resultado.value.reference !== undefined) {
          setMedia(resultado.value);
        }
      })
      .catch(() => {
        // A média é ajuda: sem ela, o índice continua como era.
      });
    return () => {
      vivo = false;
    };
  }, [container, session, projectId, podeEditar]);

  return media;
}
